import { useState, useEffect, useCallback } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import { api } from '../api';

/* ── Types ─────────────────────────────────────────────────────────── */

type WalkaroundTab = 'dashboard' | 'history' | 'defects' | 'compliance' | 'settings';

interface WalkaroundStats {
  checks_today: number;
  total_checks: number;
  open_defects: number;
  roadworthy_defects: number;
  vehicles_checked_today: number;
  total_vehicles: number;
  drivers_not_checked_today: { id: number; name: string }[];
  completion_pct: number;
}

interface WalkaroundCheck {
  id: number;
  vehicle_id: number;
  vehicle_registration: string | null;
  vehicle_make_model: string | null;
  driver_id: number;
  driver_name: string | null;
  trailer_registration: string | null;
  check_type: string | null;
  mileage_km: number | null;
  fuel_level_pct: number | null;
  adblue_level_pct: number | null;
  vehicle_height: number | null;
  vehicle_height_unit: string | null;
  trailer_height: number | null;
  trailer_height_unit: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration_minutes: number | null;
  is_roadworthy: boolean;
  status: string;
  defect_count: number;
  has_signature: boolean;
  items?: any[];
  defects?: WalkaroundDefect[];
  signature_base64?: string;
  notes?: string;
  section_timings?: SectionTiming[] | null;
}

interface SectionTiming {
  area: string;
  started_at?: string;
  completed_at?: string;
  duration_seconds: number;
}

interface WalkaroundDefect {
  id: number;
  check_id: number;
  vehicle_id: number;
  vehicle_registration: string | null;
  item_number: number | null;
  description: string;
  severity: string;
  status: string;
  photo_urls: string[];
  reported_by_name: string | null;
  reported_at: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  history?: { id: number; old_status: string | null; new_status: string; changed_by_name: string | null; changed_at: string | null; notes: string | null }[];
}

interface TimingAnalysis {
  check_id: number;
  driver_name: string | null;
  vehicle_registration: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration_minutes: number | null;
  is_flagged: boolean;
  flag_reason: string | null;
  section_timings: SectionTiming[] | null;
  tacho: {
    available: boolean;
    other_work_minutes: number;
    activities_during_check: { activity_type: string; start_time: string; duration_minutes: number; card_status: string }[];
  };
}

interface ComplianceStats {
  period_days: number;
  total_checks: number;
  overall_avg_duration_minutes: number | null;
  total_flagged: number;
  driver_summary: {
    driver_id: number;
    driver_name: string;
    check_count: number;
    avg_duration_minutes: number | null;
    min_duration_minutes: number | null;
    max_duration_minutes: number | null;
    flagged_count: number;
    compliance_score: number;
  }[];
  flagged_checks: {
    check_id: number;
    driver_id: number;
    driver_name: string;
    vehicle_registration: string | null;
    completed_at: string | null;
    duration_minutes: number;
    defect_count: number;
    is_roadworthy: boolean;
  }[];
  tacho_reconciliation: {
    check_id: number;
    driver_name: string;
    vehicle_registration: string | null;
    completed_at: string | null;
    duration_minutes: number;
    tacho_available: boolean;
    tacho_other_work_minutes: number | null;
    discrepancy: boolean;
  }[];
}

/* ── Constants ────────────────────────────────────────────────────── */

const DEFECT_STATUS_COLORS: Record<string, string> = {
  open: 'bg-red-500',
  acknowledged: 'bg-orange-500',
  in_progress: 'bg-amber-500',
  awaiting_parts: 'bg-blue-500',
  deferred: 'bg-gray-400',
  resolved: 'bg-green-500',
  rejected: 'bg-gray-500',
};

const DEFECT_STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  in_progress: 'In Progress',
  awaiting_parts: 'Awaiting Parts',
  deferred: 'Deferred',
  resolved: 'Resolved',
  rejected: 'Rejected',
};

const SEVERITY_BADGE: Record<string, { bg: string; text: string }> = {
  roadworthy: { bg: 'bg-red-100 border-red-300', text: 'text-red-800' },
  non_critical: { bg: 'bg-amber-100 border-amber-300', text: 'text-amber-800' },
};

const ALL_STATUSES = ['open', 'acknowledged', 'in_progress', 'awaiting_parts', 'deferred', 'resolved', 'rejected'];

/* ── Helpers ──────────────────────────────────────────────────────── */

// Resolve photo URLs — they come as /uploads/walkaround/xxx.jpg from the API
// VITE_API_URL is the base domain (no /api suffix), e.g. https://tourx-production.up.railway.app
// When served from same origin, VITE_API_URL is empty and relative URLs work directly
const API_ORIGIN = import.meta.env.VITE_API_URL || '';

function photoUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  // Ensure no double slashes
  const base = API_ORIGIN.replace(/\/$/, '');
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${base}${path}`;
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return d; }
}

function fmtTime(d: string | null): string {
  if (!d) return '';
  try {
    const dt = new Date(d);
    return dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function fmtDateTime(d: string | null): string {
  if (!d) return '—';
  return `${fmtDate(d)} ${fmtTime(d)}`;
}

function timeAgo(d: string | null): string {
  if (!d) return '';
  const ms = Date.now() - new Date(d).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function fmtDuration(mins: number | null | undefined): string {
  if (mins == null) return '—';
  if (mins < 1) return '<1 min';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
}

function durationColor(mins: number | null | undefined): string {
  if (mins == null) return 'text-gray-400';
  if (mins < 10) return 'text-red-700 font-bold';
  if (mins < 15) return 'text-amber-700 font-semibold';
  return 'text-green-700';
}

/* ══════════════════════════════════════════════════════════════════════
   PHOTO LIGHTBOX
   ══════════════════════════════════════════════════════════════════════ */

function PhotoLightbox({ urls, initialIndex = 0, onClose }: { urls: string[]; initialIndex?: number; onClose: () => void }) {
  const [idx, setIdx] = useState(initialIndex);
  const url = urls[idx];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={onClose}>
      <div className="relative max-w-4xl max-h-[90vh] w-full mx-4" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/80 hover:text-white text-2xl font-light">✕</button>
        <img src={photoUrl(url)} alt={`Photo ${idx + 1}`} className="w-full h-auto max-h-[85vh] object-contain rounded-lg" />
        {urls.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
            <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}
              className="px-3 py-1.5 bg-white/20 text-white rounded-lg hover:bg-white/30 disabled:opacity-30 text-sm backdrop-blur-sm">
              ← Prev
            </button>
            <span className="text-white/80 text-sm">{idx + 1} / {urls.length}</span>
            <button onClick={() => setIdx(Math.min(urls.length - 1, idx + 1))} disabled={idx === urls.length - 1}
              className="px-3 py-1.5 bg-white/20 text-white rounded-lg hover:bg-white/30 disabled:opacity-30 text-sm backdrop-blur-sm">
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Photo Thumbnail Grid ────────────────────────────────────────── */

function PhotoGrid({ urls, label }: { urls: string[]; label?: string }) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  if (!urls || urls.length === 0) return null;

  return (
    <div>
      {label && <span className="text-xs text-gray-500 uppercase block mb-1.5">{label}</span>}
      <div className="flex flex-wrap gap-2">
        {urls.map((url, i) => (
          <button key={i} onClick={() => setLightboxIdx(i)}
            className="w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-colors flex-shrink-0 bg-gray-100">
            <img src={photoUrl(url)} alt={`Photo ${i + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </button>
        ))}
      </div>
      {lightboxIdx !== null && (
        <PhotoLightbox urls={urls} initialIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      )}
    </div>
  );
}

/* ── Duration Badge ──────────────────────────────────────────────── */

function DurationBadge({ minutes, size = 'sm' }: { minutes: number | null | undefined; size?: 'sm' | 'md' }) {
  if (minutes == null) return <span className="text-gray-400 text-xs">—</span>;
  const isFlagged = minutes < 15;
  const isWarning = minutes >= 10 && minutes < 15;

  if (size === 'md') {
    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${
        isFlagged && !isWarning ? 'bg-red-50 border-red-300 text-red-800' :
        isWarning ? 'bg-amber-50 border-amber-300 text-amber-800' :
        'bg-green-50 border-green-300 text-green-800'
      }`}>
        {isFlagged && <span>⚠</span>}
        {fmtDuration(minutes)}
      </div>
    );
  }

  return (
    <span className={`text-sm ${durationColor(minutes)}`}>
      {isFlagged && '⚠ '}{fmtDuration(minutes)}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   DASHBOARD TAB
   ══════════════════════════════════════════════════════════════════════ */

function DashboardTab() {
  const [stats, setStats] = useState<WalkaroundStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getWalkaroundStats()
      .then(setStats)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" /></div>;
  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">{error}</div>;
  if (!stats) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Checks Today" value={stats.checks_today} icon="📋" />
        <KpiCard label="Fleet Completion" value={`${stats.completion_pct}%`}
          sub={`${stats.vehicles_checked_today} / ${stats.total_vehicles} vehicles`}
          color={stats.completion_pct >= 80 ? 'green' : stats.completion_pct >= 50 ? 'amber' : 'red'} icon="🚛" />
        <KpiCard label="Open Defects" value={stats.open_defects}
          color={stats.open_defects > 0 ? 'amber' : 'green'} icon="⚠️" />
        <KpiCard label="Roadworthy Issues" value={stats.roadworthy_defects}
          color={stats.roadworthy_defects > 0 ? 'red' : 'green'} icon="🛑" />
      </div>

      {stats.drivers_not_checked_today.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
              Drivers Not Yet Checked Today ({stats.drivers_not_checked_today.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {stats.drivers_not_checked_today.map(d => (
              <div key={d.id} className="px-5 py-3 flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-red-400 flex-shrink-0" />
                <span className="text-sm text-gray-700">{d.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.drivers_not_checked_today.length === 0 && stats.checks_today > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <span className="text-2xl">✅</span>
          <p className="text-green-800 font-medium mt-2">All drivers have completed their walkaround checks today</p>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, color = 'blue', icon }: { label: string; value: string | number; sub?: string; color?: string; icon?: string }) {
  const border = color === 'red' ? 'border-red-300' : color === 'amber' ? 'border-amber-300' : color === 'green' ? 'border-green-300' : 'border-blue-300';
  return (
    <div className={`bg-white border ${border} rounded-lg p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        {icon && <span className="text-2xl opacity-50">{icon}</span>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   HISTORY TAB
   ══════════════════════════════════════════════════════════════════════ */

function HistoryTab() {
  const [checks, setChecks] = useState<WalkaroundCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<WalkaroundCheck | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.getWalkaroundChecks({ limit: 100 })
      .then(setChecks)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const toggleExpand = useCallback(async (id: number) => {
    if (expandedId === id) { setExpandedId(null); setDetail(null); return; }
    setExpandedId(id);
    try {
      const d = await api.getWalkaroundCheck(id);
      setDetail(d);
    } catch (e: any) { setError(e.message); }
  }, [expandedId]);

  const filtered = checks.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.vehicle_registration || '').toLowerCase().includes(s)
      || (c.driver_name || '').toLowerCase().includes(s);
  });

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" /></div>;
  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">{error}</div>;

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <input type="text" placeholder="Search by vehicle or driver..." value={search} onChange={e => setSearch(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No walkaround checks recorded yet</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vehicle</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Driver</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Result</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Defects</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Mileage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(c => (
                <CheckRow key={c.id} check={c} isExpanded={expandedId === c.id} detail={detail} onToggle={toggleExpand} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* Extracted row component to avoid nested tbody */
function CheckRow({ check: c, isExpanded, detail, onToggle }: {
  check: WalkaroundCheck; isExpanded: boolean; detail: WalkaroundCheck | null; onToggle: (id: number) => void;
}) {
  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => onToggle(c.id)}>
        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{fmtDateTime(c.completed_at)}</td>
        <td className="px-4 py-3 text-sm font-medium text-gray-900">{c.vehicle_registration || '—'}</td>
        <td className="px-4 py-3 text-sm text-gray-700">{c.driver_name || '—'}</td>
        <td className="px-4 py-3 text-sm text-gray-500 capitalize">{c.check_type || '—'}</td>
        <td className="px-4 py-3">
          {c.is_roadworthy
            ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">Roadworthy</span>
            : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">Not Roadworthy</span>
          }
        </td>
        <td className="px-4 py-3 text-sm text-gray-700">{c.defect_count || 0}</td>
        <td className="px-4 py-3"><DurationBadge minutes={c.duration_minutes} /></td>
        <td className="px-4 py-3 text-sm text-gray-500">{c.mileage_km ? `${Math.round(c.mileage_km).toLocaleString()} km` : '—'}</td>
      </tr>
      {isExpanded && detail && (
        <tr>
          <td colSpan={8} className="px-0 py-0">
            <CheckDetail check={detail} />
          </td>
        </tr>
      )}
    </>
  );
}

function CheckDetail({ check }: { check: WalkaroundCheck }) {
  const [timingAnalysis, setTimingAnalysis] = useState<TimingAnalysis | null>(null);
  const [loadingTiming, setLoadingTiming] = useState(false);

  useEffect(() => {
    setLoadingTiming(true);
    api.getTimingAnalysis(check.id)
      .then(setTimingAnalysis)
      .catch(() => {})
      .finally(() => setLoadingTiming(false));
  }, [check.id]);

  const items = check.items || [];
  const defects = check.defects || [];
  const passed = items.filter(i => i.result === 'pass').length;
  const failed = items.filter(i => i.result === 'fail').length;
  const na = items.filter(i => i.result === 'na').length;

  // Collect all photos from items
  const itemPhotos: string[] = items
    .filter((i: any) => i.photo_url)
    .map((i: any) => i.photo_url);

  return (
    <div className="bg-slate-50 border-t border-gray-200 px-6 py-5 space-y-5">
      {/* Summary bar */}
      <div className="flex flex-wrap items-center gap-6 text-sm">
        <span className="text-green-700 font-medium">✓ {passed} passed</span>
        <span className="text-red-700 font-medium">✗ {failed} failed</span>
        <span className="text-gray-500">— {na} n/a</span>
        {check.has_signature && <span className="text-blue-600">✍ Signed</span>}
      </div>

      {/* Timing & data bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div>
            <span className="text-xs text-gray-500 uppercase block">Duration</span>
            <DurationBadge minutes={check.duration_minutes} size="md" />
          </div>
          <div>
            <span className="text-xs text-gray-500 uppercase block">Started</span>
            <p className="text-sm font-medium text-gray-800">{fmtTime(check.started_at) || '—'}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500 uppercase block">Completed</span>
            <p className="text-sm font-medium text-gray-800">{fmtTime(check.completed_at) || '—'}</p>
          </div>
          {check.mileage_km != null && (
            <div>
              <span className="text-xs text-gray-500 uppercase block">Mileage</span>
              <p className="text-sm font-medium text-gray-800">{Math.round(check.mileage_km).toLocaleString()} km</p>
            </div>
          )}
          {check.fuel_level_pct != null && (
            <div>
              <span className="text-xs text-gray-500 uppercase block">Fuel</span>
              <p className="text-sm font-medium text-gray-800">{check.fuel_level_pct}%</p>
            </div>
          )}
          {check.adblue_level_pct != null && (
            <div>
              <span className="text-xs text-gray-500 uppercase block">AdBlue</span>
              <p className="text-sm font-medium text-gray-800">{check.adblue_level_pct}%</p>
            </div>
          )}
        </div>
      </div>

      {/* Timing flag warning */}
      {check.duration_minutes != null && check.duration_minutes < 15 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <span className="text-red-500 text-lg flex-shrink-0">⚠</span>
          <div>
            <p className="text-sm font-semibold text-red-800">Quick Check Warning</p>
            <p className="text-sm text-red-700 mt-0.5">
              This walkaround was completed in {fmtDuration(check.duration_minutes)} — under the 15-minute minimum expected for a thorough DVSA-compliant inspection.
            </p>
          </div>
        </div>
      )}

      {/* Tacho cross-reference */}
      {timingAnalysis && !loadingTiming && (
        <div className={`border rounded-lg p-4 ${
          timingAnalysis.tacho.available ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'
        }`}>
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Tachograph Cross-Reference</h4>
          {timingAnalysis.tacho.available ? (
            <div className="space-y-3">
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <span className="text-xs text-gray-500">Walkaround Duration</span>
                  <p className={`font-medium ${durationColor(timingAnalysis.duration_minutes)}`}>
                    {fmtDuration(timingAnalysis.duration_minutes)}
                  </p>
                </div>
                <div className="text-gray-300 text-lg">vs</div>
                <div>
                  <span className="text-xs text-gray-500">Other Work on Tacho</span>
                  <p className="font-medium text-gray-800">{fmtDuration(timingAnalysis.tacho.other_work_minutes)}</p>
                </div>
              </div>
              {timingAnalysis.tacho.activities_during_check.length > 0 && (
                <div>
                  <span className="text-xs text-gray-500 uppercase block mb-1">Tacho Activities During Check Window</span>
                  <div className="flex flex-wrap gap-2">
                    {timingAnalysis.tacho.activities_during_check.map((a, i) => (
                      <span key={i} className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${
                        a.activity_type === 'other_work' ? 'bg-blue-50 border-blue-200 text-blue-700' :
                        a.activity_type === 'driving' ? 'bg-green-50 border-green-200 text-green-700' :
                        a.activity_type === 'rest' ? 'bg-gray-50 border-gray-200 text-gray-600' :
                        'bg-amber-50 border-amber-200 text-amber-700'
                      }`}>
                        {a.activity_type === 'other_work' ? 'Other Work' :
                         a.activity_type === 'driving' ? 'Driving' :
                         a.activity_type === 'rest' ? 'Rest' : 'Available'}
                        {' '}{a.duration_minutes}min
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* Discrepancy warning */}
              {timingAnalysis.duration_minutes != null && timingAnalysis.duration_minutes < 15 && timingAnalysis.tacho.other_work_minutes < 10 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
                  <strong>Discrepancy:</strong> Short walkaround with minimal "other work" logged on tachograph. Driver may not have recorded the check as working time.
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No tachograph data available for this driver on this date. Upload a card download covering {fmtDate(check.started_at)} to see the cross-reference.</p>
          )}
        </div>
      )}

      {/* Section timings (if app provides them) */}
      {check.section_timings && check.section_timings.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Section Breakdown</h4>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {check.section_timings.map((st, i) => {
              const totalSec = check.section_timings!.reduce((s, t) => s + t.duration_seconds, 0);
              const pct = totalSec > 0 ? (st.duration_seconds / totalSec * 100) : 0;
              return (
                <div key={i} className="flex items-center px-4 py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-sm text-gray-700 w-40">{st.area}</span>
                  <div className="flex-1 mx-3 bg-gray-100 rounded-full h-3 overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-full transition-all" style={{ width: `${Math.max(pct, 2)}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 w-16 text-right">{Math.round(st.duration_seconds / 60)}m {st.duration_seconds % 60}s</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Height & trailer info */}
      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
        {check.vehicle_height != null && (
          <span>Vehicle height: <strong>{check.vehicle_height} {check.vehicle_height_unit === 'imperial' ? 'ft' : 'm'}</strong></span>
        )}
        {check.trailer_height != null && (
          <span>Trailer height: <strong>{check.trailer_height} {check.trailer_height_unit === 'imperial' ? 'ft' : 'm'}</strong></span>
        )}
        {check.trailer_registration && (
          <span>Trailer: <strong>{check.trailer_registration}</strong></span>
        )}
        {check.notes && (
          <span>Notes: <em>{check.notes}</em></span>
        )}
      </div>

      {/* Check item photos */}
      {itemPhotos.length > 0 && (
        <PhotoGrid urls={itemPhotos} label={`Check Photos (${itemPhotos.length})`} />
      )}

      {/* Item grid */}
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Checklist Items</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
          {items.map((item: any) => (
            <div key={item.item_number} className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
              item.result === 'pass' ? 'text-green-700' : item.result === 'fail' ? 'text-red-700 bg-red-50 border border-red-100' : 'text-gray-400'
            }`}>
              <span className="w-4 text-center font-mono font-bold">{item.result === 'pass' ? '✓' : item.result === 'fail' ? '✗' : '—'}</span>
              <span className="text-gray-500 w-5 text-right font-mono">{item.item_number}.</span>
              <span className="flex-1">{item.item_text}</span>
              {item.photo_url && (
                <PhotoGrid urls={[item.photo_url]} />
              )}
              {item.notes && (
                <span className="text-gray-400 italic ml-1 truncate max-w-[120px]" title={item.notes}>{item.notes}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Defects */}
      {defects.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Defects Reported ({defects.length})</h4>
          <div className="space-y-3">
            {defects.map((d: WalkaroundDefect) => (
              <div key={d.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-2 mb-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${SEVERITY_BADGE[d.severity]?.bg || 'bg-gray-100'} ${SEVERITY_BADGE[d.severity]?.text || 'text-gray-800'}`}>
                    {d.severity === 'roadworthy' ? 'Roadworthy' : 'Non-critical'}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white ${DEFECT_STATUS_COLORS[d.status] || 'bg-gray-400'}`}>
                    {DEFECT_STATUS_LABELS[d.status] || d.status}
                  </span>
                  {d.item_number && <span className="text-xs text-gray-400">Item #{d.item_number}</span>}
                </div>
                <p className="text-sm text-gray-700">{d.description}</p>
                {d.photo_urls.length > 0 && (
                  <div className="mt-3">
                    <PhotoGrid urls={d.photo_urls} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Signature */}
      {check.signature_base64 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Driver Signature</h4>
          <div className="bg-white border border-gray-200 rounded-lg p-3 inline-block">
            <img src={`data:image/png;base64,${check.signature_base64}`} alt="Driver signature" className="h-16" />
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   DEFECTS TAB
   ══════════════════════════════════════════════════════════════════════ */

function DefectsTab() {
  const [defects, setDefects] = useState<WalkaroundDefect[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('open,acknowledged,in_progress,awaiting_parts,deferred');
  const [selectedDefect, setSelectedDefect] = useState<WalkaroundDefect | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const loadDefects = useCallback(() => {
    setLoading(true);
    api.getWalkaroundDefects({ status_filter: statusFilter || undefined })
      .then(setDefects)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { loadDefects(); }, [loadDefects]);

  const handleStatusUpdate = async () => {
    if (!selectedDefect || !newStatus) return;
    setSaving(true);
    try {
      const updated = await api.updateDefectStatus(selectedDefect.id, { status: newStatus, notes: statusNotes || undefined });
      setSelectedDefect(updated);
      setNewStatus('');
      setStatusNotes('');
      loadDefects();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleAddNote = async () => {
    if (!selectedDefect || !statusNotes.trim()) return;
    setSaving(true);
    try {
      await api.addDefectNote(selectedDefect.id, statusNotes);
      const updated = await api.getWalkaroundDefect(selectedDefect.id);
      setSelectedDefect(updated);
      setStatusNotes('');
      loadDefects();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (loading && defects.length === 0) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-4">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{error}</div>}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setStatusFilter('open,acknowledged,in_progress,awaiting_parts,deferred')}
          className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${statusFilter.includes('open') && !statusFilter.includes('resolved') ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
          Active
        </button>
        <button onClick={() => setStatusFilter('resolved,rejected')}
          className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${statusFilter.includes('resolved') ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
          Resolved
        </button>
        <button onClick={() => setStatusFilter('')}
          className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${statusFilter === '' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
          All
        </button>
      </div>

      <div className="flex gap-6">
        <div className={`${selectedDefect ? 'w-1/2' : 'w-full'} space-y-3 transition-all`}>
          {defects.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No defects found</div>
          ) : defects.map(d => (
            <div key={d.id}
              className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-sm ${selectedDefect?.id === d.id ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200'}`}
              onClick={() => { setSelectedDefect(d); setNewStatus(''); setStatusNotes(''); }}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{d.vehicle_registration || 'Unknown'}</span>
                  {d.item_number && <span className="text-xs text-gray-400">Item #{d.item_number}</span>}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${SEVERITY_BADGE[d.severity]?.bg || 'bg-gray-100'} ${SEVERITY_BADGE[d.severity]?.text || 'text-gray-800'}`}>
                    {d.severity === 'roadworthy' ? 'Roadworthy' : 'Non-critical'}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white ${DEFECT_STATUS_COLORS[d.status] || 'bg-gray-400'}`}>
                    {DEFECT_STATUS_LABELS[d.status] || d.status}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-700 line-clamp-2">{d.description}</p>
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                <span>Reported by {d.reported_by_name || 'Unknown'}</span>
                <span>{timeAgo(d.reported_at)}</span>
                {d.photo_urls.length > 0 && <span>📷 {d.photo_urls.length}</span>}
              </div>
              {d.photo_urls.length > 0 && (
                <div className="flex gap-1.5 mt-2">
                  {d.photo_urls.slice(0, 4).map((url, i) => (
                    <div key={i} className="w-12 h-12 rounded overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0">
                      <img src={photoUrl(url)} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    </div>
                  ))}
                  {d.photo_urls.length > 4 && <span className="text-xs text-gray-400 self-end">+{d.photo_urls.length - 4} more</span>}
                </div>
              )}
            </div>
          ))}
        </div>

        {selectedDefect && (
          <div className="w-1/2 bg-white border border-gray-200 rounded-lg p-5 sticky top-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Defect #{selectedDefect.id}</h3>
              <button onClick={() => setSelectedDefect(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            <div className="space-y-4">
              <div>
                <span className="text-xs text-gray-500 uppercase">Vehicle</span>
                <p className="text-sm font-medium">{selectedDefect.vehicle_registration}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase">Description</span>
                <p className="text-sm text-gray-700">{selectedDefect.description}</p>
              </div>
              <div className="flex gap-4">
                <div>
                  <span className="text-xs text-gray-500 uppercase">Severity</span>
                  <p className={`text-sm font-medium ${selectedDefect.severity === 'roadworthy' ? 'text-red-700' : 'text-amber-700'}`}>
                    {selectedDefect.severity === 'roadworthy' ? 'Roadworthy (safety-critical)' : 'Non-critical'}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase">Status</span>
                  <p className="text-sm">{DEFECT_STATUS_LABELS[selectedDefect.status] || selectedDefect.status}</p>
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase">Reported</span>
                <p className="text-sm">{selectedDefect.reported_by_name} — {fmtDateTime(selectedDefect.reported_at)}</p>
              </div>

              {selectedDefect.photo_urls.length > 0 && (
                <PhotoGrid urls={selectedDefect.photo_urls} label="Evidence Photos" />
              )}

              {selectedDefect.resolution_notes && (
                <div>
                  <span className="text-xs text-gray-500 uppercase">Resolution</span>
                  <p className="text-sm text-gray-700">{selectedDefect.resolution_notes}</p>
                </div>
              )}

              {!['resolved', 'rejected'].includes(selectedDefect.status) && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Update Status</h4>
                  <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white mb-2">
                    <option value="">Select new status...</option>
                    {ALL_STATUSES.filter(s => s !== selectedDefect.status).map(s => (
                      <option key={s} value={s}>{DEFECT_STATUS_LABELS[s]}</option>
                    ))}
                  </select>
                  <textarea value={statusNotes} onChange={e => setStatusNotes(e.target.value)}
                    placeholder="Add notes (required for status changes)..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none" rows={3} />
                  <div className="flex gap-2 mt-2">
                    {newStatus && (
                      <button onClick={handleStatusUpdate} disabled={saving || !statusNotes.trim()}
                        className="px-4 py-2 bg-slate-800 text-white text-sm rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors">
                        {saving ? 'Saving...' : `Update to ${DEFECT_STATUS_LABELS[newStatus]}`}
                      </button>
                    )}
                    {!newStatus && statusNotes.trim() && (
                      <button onClick={handleAddNote} disabled={saving}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-500 disabled:opacity-50 transition-colors">
                        {saving ? 'Saving...' : 'Add Note'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {selectedDefect.history && selectedDefect.history.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Audit Trail</h4>
                  <div className="space-y-2">
                    {selectedDefect.history.map(h => (
                      <div key={h.id} className="flex items-start gap-2 text-xs">
                        <span className="text-gray-400 whitespace-nowrap">{fmtDateTime(h.changed_at)}</span>
                        <div>
                          <span className="font-medium text-gray-700">{h.changed_by_name || 'System'}</span>
                          {h.old_status !== h.new_status ? (
                            <span className="text-gray-500"> changed status to <span className="font-medium">{DEFECT_STATUS_LABELS[h.new_status] || h.new_status}</span></span>
                          ) : (
                            <span className="text-gray-500"> added a note</span>
                          )}
                          {h.notes && <p className="text-gray-600 mt-0.5 italic">"{h.notes}"</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   COMPLIANCE TAB
   ══════════════════════════════════════════════════════════════════════ */

function ComplianceTab() {
  const [stats, setStats] = useState<ComplianceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState(30);
  const [activePanel, setActivePanel] = useState<'overview' | 'flagged' | 'tacho'>('overview');

  useEffect(() => {
    setLoading(true);
    api.getComplianceStats(period)
      .then(setStats)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" /></div>;
  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">{error}</div>;
  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Period selector & KPIs */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {[7, 14, 30, 60].map(d => (
            <button key={d} onClick={() => setPeriod(d)}
              className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
                period === d ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}>
              {d} days
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Checks" value={stats.total_checks} icon="📋" />
        <KpiCard label="Avg Duration" value={stats.overall_avg_duration_minutes ? `${stats.overall_avg_duration_minutes} min` : '—'} icon="⏱" />
        <KpiCard label="Flagged (<15 min)" value={stats.total_flagged}
          color={stats.total_flagged > 0 ? 'red' : 'green'} icon="⚠️"
          sub={stats.total_checks > 0 ? `${Math.round(stats.total_flagged / stats.total_checks * 100)}% of all checks` : undefined} />
        <KpiCard label="Drivers Analysed" value={stats.driver_summary.length} icon="👤" />
      </div>

      {/* Sub-navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {(['overview', 'flagged', 'tacho'] as const).map(p => (
          <button key={p} onClick={() => setActivePanel(p)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activePanel === p ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}>
            {p === 'overview' ? 'Driver Analysis' : p === 'flagged' ? `Flagged Checks (${stats.total_flagged})` : 'Tacho Reconciliation'}
          </button>
        ))}
      </div>

      {/* Driver Analysis Panel */}
      {activePanel === 'overview' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
              Driver Walkaround Performance — Last {period} Days
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Ranked by compliance score (worst first). Checks under 15 minutes are flagged.</p>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Driver</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Checks</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Avg Duration</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Shortest</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Longest</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Flagged</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stats.driver_summary.map(ds => (
                <tr key={ds.driver_id} className={`hover:bg-gray-50 ${ds.flagged_count > 0 ? 'bg-red-50/30' : ''}`}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{ds.driver_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 text-center">{ds.check_count}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm ${durationColor(ds.avg_duration_minutes)}`}>
                      {ds.avg_duration_minutes ? `${ds.avg_duration_minutes} min` : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm ${durationColor(ds.min_duration_minutes)}`}>
                      {ds.min_duration_minutes ? `${ds.min_duration_minutes} min` : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 text-center">
                    {ds.max_duration_minutes ? `${ds.max_duration_minutes} min` : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {ds.flagged_count > 0 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                        {ds.flagged_count}
                      </span>
                    ) : (
                      <span className="text-xs text-green-600">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                      ds.compliance_score >= 90 ? 'bg-green-100 text-green-800' :
                      ds.compliance_score >= 70 ? 'bg-amber-100 text-amber-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {ds.compliance_score}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {stats.driver_summary.length === 0 && (
            <div className="text-center py-8 text-gray-500">No checks recorded in this period</div>
          )}
        </div>
      )}

      {/* Flagged Checks Panel */}
      {activePanel === 'flagged' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 bg-red-50 border-b border-red-100">
            <h3 className="text-sm font-semibold text-red-800 uppercase tracking-wider">
              Checks Under 15 Minutes — Last {period} Days
            </h3>
            <p className="text-xs text-red-600 mt-0.5">These checks may not have been thorough enough for DVSA compliance. A proper walkaround should take 15-30 minutes.</p>
          </div>
          {stats.flagged_checks.length === 0 ? (
            <div className="text-center py-8 text-green-600 font-medium">No flagged checks — all walkarounds met the minimum duration</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Driver</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vehicle</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Duration</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Defects Found</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stats.flagged_checks.map(fc => (
                  <tr key={fc.check_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">{fmtDateTime(fc.completed_at)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{fc.driver_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fc.vehicle_registration || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm text-red-700 font-bold">⚠ {fc.duration_minutes} min</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-center">{fc.defect_count}</td>
                    <td className="px-4 py-3 text-center">
                      {fc.is_roadworthy
                        ? <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200">Pass</span>
                        : <span className="text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded border border-red-200">Fail</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tacho Reconciliation Panel */}
      {activePanel === 'tacho' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              This view compares walkaround check duration with "Other Work" recorded on the tachograph for the same date.
              Discrepancies may indicate the driver is not recording the walkaround as working time, or is rushing through checks.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Tacho vs Walkaround — Flagged Checks
              </h3>
            </div>
            {stats.tacho_reconciliation.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No flagged checks with tacho data to reconcile</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Driver</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vehicle</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Walkaround Duration</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Tacho Other Work</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stats.tacho_reconciliation.map(tr => (
                    <tr key={tr.check_id} className={`hover:bg-gray-50 ${tr.discrepancy ? 'bg-amber-50/30' : ''}`}>
                      <td className="px-4 py-3 text-sm text-gray-700">{fmtDateTime(tr.completed_at)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{tr.driver_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{tr.vehicle_registration || '—'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm text-red-700 font-bold">{tr.duration_minutes} min</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {tr.tacho_available ? (
                          <span className="text-sm font-medium text-gray-800">{tr.tacho_other_work_minutes} min</span>
                        ) : (
                          <span className="text-xs text-gray-400">No tacho data</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {!tr.tacho_available ? (
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">No Data</span>
                        ) : tr.discrepancy ? (
                          <span className="text-xs text-amber-800 bg-amber-100 px-2 py-0.5 rounded border border-amber-200 font-medium">Discrepancy</span>
                        ) : (
                          <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   SETTINGS TAB
   ══════════════════════════════════════════════════════════════════════ */

function SettingsTab() {
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [photoCount, setPhotoCount] = useState(3);
  const [reminderHours, setReminderHours] = useState(24);
  const [requireTrailer, setRequireTrailer] = useState(false);

  useEffect(() => {
    api.getWalkaroundConfig()
      .then(c => {
        setConfig(c);
        setPhotoCount(c.random_photo_count || 3);
        setReminderHours(c.check_reminder_hours || 24);
        setRequireTrailer(c.require_trailer_check || false);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateWalkaroundConfig({
        random_photo_count: photoCount,
        check_reminder_hours: reminderHours,
        require_trailer_check: requireTrailer,
      });
      setError('');
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="max-w-2xl space-y-6">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{error}</div>}

      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Walkaround Configuration</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Random Photo Count</label>
          <p className="text-xs text-gray-500 mb-2">Number of checklist items that require a photo per check (randomly selected)</p>
          <input type="number" min={0} max={10} value={photoCount} onChange={e => setPhotoCount(Number(e.target.value))}
            className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Overdue Reminder (hours)</label>
          <p className="text-xs text-gray-500 mb-2">Alert if a driver hasn't completed a check within this many hours</p>
          <input type="number" min={1} max={72} value={reminderHours} onChange={e => setReminderHours(Number(e.target.value))}
            className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>

        <div className="flex items-center gap-3">
          <input type="checkbox" id="requireTrailer" checked={requireTrailer} onChange={e => setRequireTrailer(e.target.checked)}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
          <label htmlFor="requireTrailer" className="text-sm text-gray-700">
            Always prompt for trailer check (even if driver selects "No trailer")
          </label>
        </div>

        <button onClick={handleSave} disabled={saving}
          className="px-5 py-2 bg-slate-800 text-white text-sm rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors">
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      {config?.checklist && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
              DVSA Standard Checklist ({config.checklist.length} items)
            </h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Check Item</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Area</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Severity</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Photo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {config.checklist.map((item: any, i: number) => (
                  <tr key={item.number} className={i % 2 ? 'bg-gray-50/50' : ''}>
                    <td className="px-3 py-1.5 text-xs text-gray-500 font-mono">{item.number}</td>
                    <td className="px-3 py-1.5 text-xs text-gray-700">{item.text}</td>
                    <td className="px-3 py-1.5 text-xs text-gray-500">{item.area}</td>
                    <td className="px-3 py-1.5">
                      <span className={`text-xs font-medium ${item.severity === 'roadworthy' ? 'text-red-600' : 'text-amber-600'}`}>
                        {item.severity === 'roadworthy' ? 'Roadworthy' : 'Non-critical'}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-xs text-gray-500">{item.photo_eligible ? '📷' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════════════════════ */

const TABS: { id: WalkaroundTab; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'history', label: 'Check History' },
  { id: 'defects', label: 'Defects' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'settings', label: 'Settings' },
];

export function WalkaroundPage() {
  const [tab, setTab] = useState<WalkaroundTab>('dashboard');

  return (
    <PortalLayout currentPage="walkaround">
      <div className="flex flex-col h-screen">
        {/* Top bar */}
        <div className="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-6 flex-shrink-0">
          <div>
            <h1 className="text-sm font-bold text-gray-900">Walkaround Checks</h1>
            <p className="text-[10px] text-gray-400">DVSA-compliant daily vehicle inspection management</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b border-gray-200 px-6 flex-shrink-0">
          <div className="flex gap-0">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.id
                    ? 'border-slate-800 text-slate-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-100">
          {tab === 'dashboard' && <DashboardTab />}
          {tab === 'history' && <HistoryTab />}
          {tab === 'defects' && <DefectsTab />}
          {tab === 'compliance' && <ComplianceTab />}
          {tab === 'settings' && <SettingsTab />}
        </div>
      </div>
    </PortalLayout>
  );
}
